import usei18n from "@app-i18n/index";
import { extrasPage, foodBeveragePage, footwearsPage, offersPage } from "@app-router/routes/reservations";
import useCartStore, { CartItemCategory, ICartItem } from "@app-store/cart";
import useReservationStore from "@app-store/reservation";
import useReservationFluxStore from "@app-store/reservation-flux";
import { EventAI, trackUserEvent } from "@app-utilities/app-insights";
import { computed } from "vue";
import { useRoute } from "vue-router";

export function useBowlingCartSummary() {
	const reservationStore = useReservationStore();
	const cartStore = useCartStore();
	const route = useRoute();
	const { translateKey } = usei18n();

	const categories = computed(() => {
		return cartStore.getCategories();
	});
	const categoriesWithQuantifier = computed(() => ["food-beverage", "reservation-extras"]);
	const categoriesWithDelete = computed(() => ["food-beverage", "reservation-extras"]);

	const playersLabel = computed(() => {
		return reservationStore.bowlers
			.map((b) => {
				if (!b.Quantity) return false;
				return `${b.Quantity} ${b.Name}`;
			})
			.filter(Boolean)
			.join(", ");
	});
	const totalWithoutTaxes = computed(() => {
		return cartStore.totalWithoutTaxes();
	});

	const getItemsInCategory = (category: CartItemCategory) => {
		return cartStore.getItemsInCategory(category);
	};
	const getTotalInCategory = (category: CartItemCategory) => {
		return cartStore.getTotalInCategory(category);
	};
	const getLinkLabel = (category: CartItemCategory) => {
		return category === "bowling-experience"
			? translateKey("label_bowling")
			: category === "footwears"
				? translateKey("shoes_and_socks")
				: category === "food-beverage"
					? translateKey("food_and_beverage")
					: category === "reservation-extras"
						? translateKey("label_extras")
						: `[${category}]`;
	};
	const getFootwearLabel = (id: string | number) => {
		const item = cartStore.getItemById(id, "footwears");
		if (!item) return "";
		return item.Quantity === 1
			? translateKey("label_pair_one_qty")
			: translateKey("label_pair_qty", { qty: item.Quantity });
	};
	const getItemLabel = (category: CartItemCategory, item: ICartItem) => {
		if (category === "bowling-experience")
			return playersLabel.value;

		if (category === "footwears")
			return getFootwearLabel(item.ItemId);
	};
	const getItemName = (item: ICartItem) => {
		return item.Title;
	};
	const onBackClick = (category: CartItemCategory) => {
		const targetName = category === "bowling-experience"
			? offersPage
			: category === "footwears"
				? footwearsPage
				: category === "reservation-extras"
					? extrasPage
					: category === "food-beverage"
						? foodBeveragePage
						: null;
		if (targetName) {
			useReservationFluxStore().jumpToView({
				target: {
					...route,
					name: targetName
				}
			});
			trackUserEvent(EventAI.CartBackToStep, {
				viewName: targetName
			});
		}
	};
	const onDeleteItemFromCart = (item: ICartItem) => {
		cartStore.removeItem({
			id: item.ItemId,
			category: item.Category,
			subCategory: item.SubCategory,
			compoundKey: item.ItemCompoundKey
		});
		trackUserEvent(EventAI.CartRemoveItem, {
			title: item.Title,
			category: item.Category,
			subCategory: item.SubCategory
		});
	};
	const onQuantifierAddClick = (cartItem: ICartItem) => {
		cartStore.addItem({
			category: cartItem.Category,
			title: cartItem.Title ?? "",
			description: cartItem.Description ?? "",
			data: cartItem.ItemData,
			price: cartItem.UnitPrice ?? 0,
			modifiers: cartItem.Modifiers,
			note: cartItem.Note,
			quantity: 1
		});
		trackUserEvent(EventAI.CartIncreaseItemQuantity, {
			title: cartItem.Title ?? "",
			description: cartItem.Description ?? "",
			category: cartItem.Category,
			data: cartItem.ItemData,
			price: cartItem.UnitPrice ?? 0,
			modifiers: cartItem.Modifiers,
			note: cartItem.Note
		});
	};
	const onQuantifierSubClick = ({
		cartItem,
		showModal,
		showRemoveModal,
		deleteItemFromCart,
	}: {
        cartItem: ICartItem,
        showModal: boolean,
        showRemoveModal: (item: ICartItem) => Promise<void>,
        deleteItemFromCart: (item: ICartItem) => void
      }) => {
		const id = cartItem.ItemId;
		const category = cartItem.Category;
		const subCategory = cartItem.SubCategory;
		const compoundKey = cartItem.ItemCompoundKey;
		const item = cartStore.getItemById(
			id,
			category,
			subCategory,
			compoundKey
		);
		if (item && item.Quantity === 1) {
			if (showModal) return showRemoveModal(item);

			return deleteItemFromCart(item);
		}
		cartStore.subItem({ id, category, subCategory, compoundKey });
		trackUserEvent(EventAI.CartReduceItemQuantity, {
			title: item?.Title,
			category: item?.Category,
			subCategory: item?.SubCategory
		});

		return true;
	};

	return {
		categories,
		categoriesWithQuantifier,
		categoriesWithDelete,
		totalWithoutTaxes,
		getItemsInCategory,
		getTotalInCategory,
		getLinkLabel,
		getItemLabel,
		getItemName,
		onBackClick,
		onDeleteItemFromCart,
		onQuantifierAddClick,
		onQuantifierSubClick
	};
}
